import { useState, useCallback } from 'react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  error?: string;
}

interface UseStreamingChatReturn {
  sendStreamingMessage: (message: string, conversationHistory: Message[]) => Promise<string>;
  isStreaming: boolean;
  currentStreamingText: string;
  streamingError: string | null;
  abortStreaming: () => void;
}

export const useStreamingChat = (): UseStreamingChatReturn => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamingText, setCurrentStreamingText] = useState('');
  const [streamingError, setStreamingError] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const abortStreaming = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsStreaming(false);
    }
  }, [abortController]);

  const sendStreamingMessage = useCallback(async (
    message: string, 
    conversationHistory: Message[]
  ): Promise<string> => {
    console.log('🚀 Starting streaming message:', message);
    setIsStreaming(true);
    setCurrentStreamingText('');
    setStreamingError(null);

    const controller = new AbortController();
    setAbortController(controller);

    try {
      console.log('📡 Making fetch request to /api/chat-stream');
      const response = await fetch('/api/chat-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userMessage: message,
          conversationHistory: conversationHistory,
        }),
        signal: controller.signal,
      });

      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        console.error('❌ No reader available from response');
        throw new Error('No reader available');
      }

      console.log('📖 Starting to read stream...');
      let accumulatedText = '';
      let isJsonResponse = false;
      let jsonStartDetected = false;
      let chunkCount = 0;

      while (true) {
        console.log(`📖 Reading chunk ${++chunkCount}...`);
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ Stream reading complete, total chunks:', chunkCount);
          break;
        }

        const chunk = new TextDecoder().decode(value);
        console.log(`📝 Chunk ${chunkCount} (${chunk.length} chars):`, chunk.substring(0, 50) + '...');
        
        accumulatedText += chunk;

        // Detect if this might be a JSON response early on
        if (!jsonStartDetected && accumulatedText.length > 50) {
          // Look for JSON patterns in the accumulated text
          const hasJsonPattern = 
            accumulatedText.includes('```json') || 
            (accumulatedText.includes('"action"') && accumulatedText.includes('"update_itinerary"'));
          
          if (hasJsonPattern) {
            console.log('🎯 JSON response detected, switching to itinerary mode');
            isJsonResponse = true;
            jsonStartDetected = true;
            setCurrentStreamingText('🏗️ Creating your itinerary...');
          }
        }

        // Only show streaming text if it's not a JSON response
        if (!isJsonResponse) {
          setCurrentStreamingText(accumulatedText);
        }

        // Check for error messages in stream
        if (chunk.includes('[ERROR:')) {
          console.error('❌ Error detected in stream:', chunk);
          setStreamingError('Stream was interrupted. Partial response shown.');
        }
      }

      console.log('✅ Final accumulated text length:', accumulatedText.length);

      return accumulatedText;

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        setStreamingError('Streaming was cancelled.');
        return currentStreamingText; // Return partial text
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown streaming error';
      setStreamingError(errorMessage);
      
      // Return partial text even on error
      return currentStreamingText;
    } finally {
      setIsStreaming(false);
      setAbortController(null);
    }
  }, [currentStreamingText]);

  return {
    sendStreamingMessage,
    isStreaming,
    currentStreamingText,
    streamingError,
    abortStreaming,
  };
};